import React, { useMemo } from "react";
import type { VehicleDetailData, VehicleSummary, MaintenanceItem, MaintPlanItem } from "@/types/fleet-reports.v2";

type Period = { from: Date; to: Date };

// Tipos de seções disponíveis
export type SectionType = 'summary' | 'fuel' | 'maintenance' | 'tires' | 'expenses' | 'documents' | 'trips';

const ALL_SECTIONS: SectionType[] = ['summary', 'fuel', 'maintenance', 'tires', 'expenses', 'documents', 'trips'];

type Props = {
  rank: number;
  vehicle: VehicleDetailData | null;
  summaryFallback: VehicleSummary;
  period: Period;
  isLoading?: boolean;
  /** Seções a exibir. Default: todas */
  visibleSections?: SectionType[];
  /** Exibir header com KPIs e composição de custos. Default: true */
  showKPIs?: boolean;
};

const moneyBRL = (n: number) =>
  (n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const numberBR = (n: number) => (n || 0).toLocaleString("pt-BR");

// ============ TRADUÇÃO DE STATUS ============
const STATUS_PT: Record<string, string> = {
  // OSs
  open: "Aberta",
  closed: "Fechada",
  in_progress: "Em Andamento",
  pending_approval: "Aguardando Aprovação",
  approved: "Aprovada",
  rejected: "Rejeitada",
  // Planos
  active: "Ativo",
  pending: "Pendente",
  completed: "Concluído",
  inactive: "Inativo",
};

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  pending_approval: "bg-orange-100 text-orange-800 border-orange-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-orange-100 text-orange-800 border-orange-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  inactive: "bg-gray-100 text-gray-600 border-gray-200",
};

// Tradução e cores para status de aprovação
const APPROVAL_PT: Record<string, string> = {
  pending: "Aguardando Aprovação",
  approved: "Aprovado",
  rejected: "Rejeitado",
};

const APPROVAL_COLORS: Record<string, string> = {
  pending: "bg-amber-200 text-amber-900 border-amber-400 animate-pulse",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const TYPE_PT: Record<string, string> = {
  preventive: "Preventiva",
  corrective: "Corretiva",
  predictive: "Preditiva",
  oil_change: "Troca de Óleo",
  other: "Outra",
};

function translateStatus(status: string | null | undefined): string {
  if (!status) return "-";
  return STATUS_PT[status.toLowerCase()] ?? status;
}

function translateType(type: string | null | undefined): string {
  if (!type) return "-";
  return TYPE_PT[type.toLowerCase()] ?? type;
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">-</span>;
  const key = status.toLowerCase();
  const colorClass = STATUS_COLORS[key] ?? "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
      {translateStatus(status)}
    </span>
  );
}

function ApprovalBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const key = status.toLowerCase();
  const label = APPROVAL_PT[key] ?? status;
  const colorClass = APPROVAL_COLORS[key] ?? "bg-gray-100 text-gray-600 border-gray-200";
  const isPending = key === "pending";
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${colorClass}`}>
      {isPending && <span>⏳</span>}
      {label}
    </span>
  );
}

type Tone = "ok" | "warning" | "critical";

function severityFromCostPerKm(costPerKm: number): { label: string; tone: Tone } {
  if (costPerKm >= 2.5) return { label: "Crítico", tone: "critical" };
  if (costPerKm >= 1.5) return { label: "Atenção", tone: "warning" };
  return { label: "OK", tone: "ok" };
}

function toneClasses(tone: Tone) {
  if (tone === "critical") return "bg-destructive text-destructive-foreground";
  if (tone === "warning") return "bg-amber-500 text-white";
  return "bg-emerald-500 text-white";
}

function Pill({ label, tone }: { label: string; tone: Tone }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${toneClasses(tone)}`}>
      {label}
    </span>
  );
}

function MiniBar({ pct, colorClass }: { pct: number; colorClass: string }) {
  return (
    <div className="w-full h-2 rounded bg-muted overflow-hidden">
      <div className={`h-full ${colorClass}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
  );
}

function DataTable({
  columns,
  rows,
  emptyLabel,
  loading,
}: {
  columns: { key: string; label: string; w?: string; align?: "left" | "right" | "center" }[];
  rows: Record<string, any>[];
  emptyLabel?: string;
  loading?: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((c) => (
              <th
                key={c.key}
                className={`py-2 px-3 text-xs font-semibold text-muted-foreground ${
                  c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"
                }`}
                style={c.w ? { width: c.w } : undefined}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <tr key={idx} className="border-b border-border/50">
                {columns.map((c) => (
                  <td key={c.key} className="py-2 px-3">
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-4 text-center text-muted-foreground text-xs">
                {emptyLabel ?? "Sem registros no período."}
              </td>
            </tr>
          ) : (
            rows.map((r, idx) => (
              <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`py-2 px-3 ${
                      c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"
                    }`}
                  >
                    {r[c.key] ?? "-"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeader({ icon, title, count }: { icon: string; title: string; count: number }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-lg">{icon}</span>
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <span className="px-2 py-0.5 rounded bg-muted text-xs text-muted-foreground font-medium">
        {count}
      </span>
    </div>
  );
}

// ============ COLUNAS PADRONIZADAS (6 colunas por tabela) ============
// Larguras fixas: Data=100px, Valor=100px, Status=120px, Qtd=60px

const COLUMNS = {
  fuel: [
    { key: "occurredAt", label: "Data", w: "100px" },
    { key: "station", label: "Posto" },
    { key: "liters", label: "Litros", align: "right" as const, w: "80px" },
    { key: "priceUnit", label: "R$/L", align: "right" as const, w: "80px" },
    { key: "totalValue", label: "Valor", align: "right" as const, w: "100px" },
    { key: "odometerKm", label: "Odômetro", align: "right" as const, w: "100px" },
  ],
  workOrders: [
    { key: "executedAt", label: "Data", w: "100px" },
    { key: "orderNumber", label: "OS", w: "90px" },
    { key: "description", label: "Descrição" },
    { key: "supplier", label: "Fornecedor" },
    { key: "totalCost", label: "Valor", align: "right" as const, w: "100px" },
    { key: "status", label: "Status", align: "center" as const, w: "120px" },
  ],
  tires: [
    { key: "occurredAt", label: "Data", w: "100px" },
    { key: "tireModel", label: "Modelo" },
    { key: "description", label: "Descrição" },
    { key: "supplier", label: "Fornecedor" },
    { key: "totalCost", label: "Valor", align: "right" as const, w: "100px" },
    { key: "qty", label: "Qtd", align: "center" as const, w: "60px" },
  ],
  expenses: [
    { key: "occurredAt", label: "Data", w: "100px" },
    { key: "category", label: "Categoria" },
    { key: "description", label: "Descrição" },
    { key: "supplier", label: "Fornecedor" },
    { key: "totalCost", label: "Valor", align: "right" as const, w: "100px" },
    { key: "status", label: "Status", align: "center" as const, w: "120px" },
  ],
  documents: [
    { key: "issuedAt", label: "Emissão", w: "100px" },
    { key: "name", label: "Documento" },
    { key: "type", label: "Tipo" },
    { key: "expiryDate", label: "Vencimento", w: "100px" },
    { key: "totalCost", label: "Valor", align: "right" as const, w: "100px" },
    { key: "status", label: "Status", align: "center" as const, w: "120px" },
  ],
  trips: [
    { key: "startAt", label: "Início", w: "100px" },
    { key: "endAt", label: "Fim", w: "100px" },
    { key: "route", label: "Rota" },
    { key: "driverName", label: "Motorista" },
    { key: "distanceKm", label: "Km", align: "right" as const, w: "80px" },
    { key: "status", label: "Status", align: "center" as const, w: "120px" },
  ],
};

// Status para Despesas
const EXPENSE_STATUS_PT: Record<string, string> = {
  pending: "Pendente",
  paid: "Pago",
  overdue: "Vencido",
  cancelled: "Cancelado",
};

const EXPENSE_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

function ExpenseStatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">-</span>;
  const key = status.toLowerCase();
  const label = EXPENSE_STATUS_PT[key] ?? status;
  const colorClass = EXPENSE_STATUS_COLORS[key] ?? "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
      {label}
    </span>
  );
}

// Status para Documentos
const DOC_STATUS_PT: Record<string, string> = {
  valid: "Válido",
  expired: "Vencido",
  expiring: "Vencendo",
  pending: "Pendente",
};

const DOC_STATUS_COLORS: Record<string, string> = {
  valid: "bg-green-100 text-green-800 border-green-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  expiring: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-blue-100 text-blue-800 border-blue-200",
};

function DocStatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">-</span>;
  const key = status.toLowerCase();
  const label = DOC_STATUS_PT[key] ?? status;
  const colorClass = DOC_STATUS_COLORS[key] ?? "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
      {label}
    </span>
  );
}

// Status para Viagens
const TRIP_STATUS_PT: Record<string, string> = {
  scheduled: "Agendada",
  in_progress: "Em Andamento",
  completed: "Concluída",
  cancelled: "Cancelada",
};

const TRIP_STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

function TripStatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">-</span>;
  const key = status.toLowerCase();
  const label = TRIP_STATUS_PT[key] ?? status;
  const colorClass = TRIP_STATUS_COLORS[key] ?? "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
      {label}
    </span>
  );
}

export default function VehicleDetailSection({
  rank,
  vehicle,
  summaryFallback,
  period,
  isLoading,
  visibleSections = ALL_SECTIONS,
  showKPIs = true,
}: Props) {
  // Helper para verificar se seção está visível
  const show = (section: SectionType) => visibleSections.includes(section);
  const merged = useMemo(() => {
    if (vehicle) return vehicle;
    return {
      ...summaryFallback,
      fuel: [],
      maintenance: [],
      maintPlans: [],
      tires: [],
      expenses: [],
      documents: [],
      trips: [],
    } as VehicleDetailData;
  }, [vehicle, summaryFallback]);

  // Agrupar OSs por planId
  const ossByPlan = useMemo(() => {
    const map = new Map<string | null, MaintenanceItem[]>();
    for (const os of merged.maintenance ?? []) {
      const planId = os.planId ?? null;
      if (!map.has(planId)) map.set(planId, []);
      map.get(planId)!.push(os);
    }
    return map;
  }, [merged.maintenance]);

  // OSs avulsas (sem plano)
  const ossAvulsas = ossByPlan.get(null) ?? [];

  const vehicleLabel = merged?.vehicle_label ?? "—";
  const totalCost = merged?.summary?.totalCost ?? 0;
  const costPerKm = merged?.summary?.costPerKm ?? 0;
  const km = merged?.summary?.km ?? 0;
  const breakdown = merged?.summary?.breakdown ?? {
    fuel: 0,
    maintenance: 0,
    tires: 0,
    expenses: 0,
    documents: 0,
    trips: 0,
  };

  const sev = severityFromCostPerKm(costPerKm);

  const counts = {
    fuel: merged?.fuel?.length ?? 0,
    maintenance: merged?.maintenance?.length ?? 0,
    maintPlans: merged?.maintPlans?.length ?? 0,
    tires: merged?.tires?.length ?? 0,
    expenses: merged?.expenses?.length ?? 0,
    documents: merged?.documents?.length ?? 0,
    trips: merged?.trips?.length ?? 0,
  };

  const costRows = [
    { k: "fuel" as const, label: "Combustível", color: "bg-amber-500" },
    { k: "maintenance" as const, label: "Manutenção", color: "bg-blue-500" },
    { k: "tires" as const, label: "Pneus", color: "bg-violet-600" },
    { k: "expenses" as const, label: "Despesas", color: "bg-emerald-500" },
    { k: "documents" as const, label: "Documentos", color: "bg-muted-foreground" },
    { k: "trips" as const, label: "Viagens", color: "bg-foreground" },
  ];

  const periodLabel = `${period.from.toLocaleDateString("pt-BR")} – ${period.to.toLocaleDateString("pt-BR")}`;

  const tableLoading = Boolean(isLoading) && !vehicle;

  // Formatar linha de OS para tabela (6 colunas padronizadas)
  const formatOsRow = (os: MaintenanceItem) => ({
    executedAt: os.executedAt,
    orderNumber: os.orderNumber,
    description: os.description || "-",
    supplier: os.supplier || "-",
    totalCost: moneyBRL(os.totalCost),
    status: <StatusBadge status={os.status} />,
  });

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden print:break-inside-avoid">
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs opacity-80 font-semibold">#{rank}</span>
              <span className="text-lg font-bold">{vehicleLabel}</span>
              <Pill label={sev.label} tone={sev.tone} />
            </div>
            <p className="text-sm opacity-80 mt-1">Período: {periodLabel}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{moneyBRL(totalCost)}</p>
            <p className="text-xs opacity-80">{moneyBRL(costPerKm)}/km • {numberBR(km)} km</p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* KPIs + Composição de custos */}
        {showKPIs && show('summary') && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted/30 rounded-xl p-4 space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Composição de Custos</h4>
              {costRows.map((r) => {
                const v = (breakdown as any)[r.k] || 0;
                const pct = totalCost > 0 ? (v / totalCost) * 100 : 0;
                return (
                  <div key={r.k} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{r.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{moneyBRL(v)}</span>
                        <span className="text-muted-foreground text-[10px]">
                          {Math.round(pct)}%
                        </span>
                      </div>
                    </div>
                    <MiniBar pct={pct} colorClass={r.color} />
                  </div>
                );
              })}
            </div>

            <div className="md:col-span-2 grid grid-cols-3 gap-3">
              <div className="bg-muted/30 rounded-xl p-3">
                <p className="text-xs text-muted-foreground">Custo Total</p>
                <p className="text-lg font-bold text-foreground">{moneyBRL(totalCost)}</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-3">
                <p className="text-xs text-muted-foreground">Custo/Km</p>
                <p className="text-lg font-bold text-foreground">{moneyBRL(costPerKm)}</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-3">
                <p className="text-xs text-muted-foreground">Km Rodados</p>
                <p className="text-lg font-bold text-foreground">{numberBR(km)}</p>
              </div>
            </div>
          </div>
        )}

        {/* ========== TABELAS ========== */}

        {/* 1. Combustível (6 colunas) */}
        {show('fuel') && (
          <div className="bg-muted/20 rounded-xl p-4">
            <SectionHeader icon="⛽" title="Combustível" count={counts.fuel} />
            <DataTable
              columns={COLUMNS.fuel}
              rows={(merged.fuel ?? []).map((f) => ({
                occurredAt: f.occurredAt,
                station: f.station || "-",
                liters: f.liters != null ? Number(f.liters).toFixed(1) : "-",
                priceUnit: f.priceUnit != null ? Number(f.priceUnit).toFixed(2) : "-",
                totalValue: moneyBRL(Number(f.totalValue ?? 0)),
                odometerKm: f.odometerKm != null ? numberBR(f.odometerKm) : "-",
              }))}
              loading={tableLoading}
            />
          </div>
        )}

        {/* 2. PLANOS DE MANUTENÇÃO E ORDENS DE SERVIÇO (Hierárquico) */}
        {show('maintenance') && (
          <div className="bg-muted/20 rounded-xl p-4">
            <SectionHeader 
              icon="📋" 
              title="Planos de Manutenção e Ordens de Serviço" 
              count={counts.maintPlans + counts.maintenance} 
            />
            
            <div className="space-y-4">
              {/* Para cada plano de manutenção */}
              {(merged.maintPlans ?? []).map((plan) => {
                const ossVinculadas = ossByPlan.get(plan.id) ?? [];
                const periodParts: string[] = [];
                if (plan.periodicityKm) periodParts.push(`${numberBR(plan.periodicityKm)} km`);
                if (plan.periodicityDays) periodParts.push(`${plan.periodicityDays} dias`);

                const nextParts: string[] = [];
                if (plan.nextExecutionKm) nextParts.push(`${numberBR(plan.nextExecutionKm)} km`);
                if (plan.nextExecutionDate) nextParts.push(plan.nextExecutionDate);

                return (
                  <div key={plan.id} className="border border-border rounded-lg overflow-hidden">
                    {/* Header do Plano */}
                    <div className="bg-primary/10 p-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">🔧</span>
                          <div>
                            <span className="font-semibold text-foreground">{plan.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({translateType(plan.type)})
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          {plan.category && (
                            <span className="text-muted-foreground">
                              Categoria: <span className="text-foreground">{plan.category}</span>
                            </span>
                          )}
                          {periodParts.length > 0 && (
                            <span className="text-muted-foreground">
                              Periodicidade: <span className="text-foreground">{periodParts.join(" / ")}</span>
                            </span>
                          )}
                          {nextParts.length > 0 && (
                            <span className="text-muted-foreground">
                              Próxima: <span className="text-foreground">{nextParts.join(" / ")}</span>
                            </span>
                          )}
                          <ApprovalBadge status={plan.approvalStatus} />
                          <StatusBadge status={plan.status} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Alerta para planos pendentes de aprovação */}
                    {plan.approvalStatus === "pending" && (
                      <div className="bg-amber-50 dark:bg-amber-900/30 border-l-4 border-amber-400 p-2 text-sm flex items-center gap-2">
                        <span>⚠️</span>
                        <span className="text-amber-800 dark:text-amber-200 font-medium">
                          Este plano aguarda aprovação para ser executado
                        </span>
                      </div>
                    )}
                    
                    {/* Tabela de OSs vinculadas */}
                    {tableLoading ? (
                      <div className="p-3">
                        <div className="h-8 bg-muted rounded animate-pulse" />
                      </div>
                    ) : ossVinculadas.length > 0 ? (
                      <DataTable
                        columns={COLUMNS.workOrders}
                        rows={ossVinculadas.map(formatOsRow)}
                      />
                    ) : (
                      <p className="p-3 text-sm text-muted-foreground italic">
                        Nenhuma OS vinculada a este plano no período
                      </p>
                    )}
                  </div>
                );
              })}

              {/* OSs Avulsas (sem plano) */}
              {ossAvulsas.length > 0 && (
                <div className="border border-amber-300 rounded-lg overflow-hidden">
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">⚠️</span>
                      <span className="font-semibold text-foreground">Ordens de Serviço Avulsas</span>
                      <span className="text-xs text-muted-foreground">
                        (sem plano vinculado)
                      </span>
                      <span className="ml-auto px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium">
                        {ossAvulsas.length}
                      </span>
                    </div>
                  </div>
                  <DataTable
                    columns={COLUMNS.workOrders}
                    rows={ossAvulsas.map(formatOsRow)}
                    loading={tableLoading}
                  />
                </div>
              )}

              {/* Mensagem se não houver planos nem OSs */}
              {counts.maintPlans === 0 && counts.maintenance === 0 && !tableLoading && (
                <p className="text-center text-muted-foreground text-sm py-4">
                  Sem planos de manutenção ou ordens de serviço no período.
                </p>
              )}
            </div>
          </div>
        )}

        {/* 3. Pneus (6 colunas) */}
        {show('tires') && (
          <div className="bg-muted/20 rounded-xl p-4">
            <SectionHeader icon="🛞" title="Pneus" count={counts.tires} />
            <DataTable
              columns={COLUMNS.tires}
              rows={(merged.tires ?? []).map((t) => ({
                occurredAt: t.occurredAt,
                tireModel: t.tireModel || "-",
                description: t.description || "-",
                supplier: t.supplier || "-",
                totalCost: moneyBRL(t.totalCost),
                qty: t.qty ?? "-",
              }))}
              loading={tableLoading}
            />
          </div>
        )}

        {/* 4. Despesas (6 colunas) */}
        {show('expenses') && (
          <div className="bg-muted/20 rounded-xl p-4">
            <SectionHeader icon="💰" title="Despesas" count={counts.expenses} />
            <DataTable
              columns={COLUMNS.expenses}
              rows={(merged.expenses ?? []).map((e) => ({
                occurredAt: e.occurredAt,
                category: e.category,
                description: e.description || e.notes || "-",
                supplier: e.supplier || "-",
                totalCost: moneyBRL(e.totalCost),
                status: <ExpenseStatusBadge status={e.status} />,
              }))}
              loading={tableLoading}
            />
          </div>
        )}

        {/* 5. Documentos (6 colunas) */}
        {show('documents') && (
          <div className="bg-muted/20 rounded-xl p-4">
            <SectionHeader icon="📄" title="Documentos" count={counts.documents} />
            <DataTable
              columns={COLUMNS.documents}
              rows={(merged.documents ?? []).map((d) => ({
                issuedAt: d.issuedAt || "-",
                name: d.name,
                type: d.type,
                expiryDate: d.expiryDate || "-",
                totalCost: d.totalCost != null ? moneyBRL(d.totalCost) : "-",
                status: <DocStatusBadge status={d.status} />,
              }))}
              loading={tableLoading}
            />
          </div>
        )}

        {/* 6. Viagens (6 colunas) */}
        {show('trips') && (
          <div className="bg-muted/20 rounded-xl p-4">
            <SectionHeader icon="🚚" title="Viagens" count={counts.trips} />
            <DataTable
              columns={COLUMNS.trips}
              rows={(merged.trips ?? []).map((t) => ({
                startAt: t.startAt,
                endAt: t.endAt || "-",
                route: [t.origin, t.destination].filter(Boolean).join(" → ") || "-",
                driverName: t.driverName || "-",
                distanceKm: numberBR(t.distanceKm),
                status: <TripStatusBadge status={t.status} />,
              }))}
              loading={tableLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
